import { computed, inject, Injectable, signal } from '@angular/core';
import {
  DEPOSIT_EXIT_BONUS_GROUP,
  ONE_PLUS_DEPOSITOR,
} from './user/data/user-group.data';
import { UserGroupsService } from './user/user-groups.service';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { StaticContentService } from './static-content.service';
import { UserService } from './user/user.service';
import { interval, of, Subscription } from 'rxjs';
import { LocalstorageService, PlatformService } from 'ngx-unificator/services';
import { AB_TEST_LIST } from '../ab-test/ab-test.data';
import { DOCUMENT } from '@angular/common';

/**
 * State interface
 */
interface CustomBonus {
  noDepositBonus: {
    isNoDepositFsBonus: boolean;
  };
  depositExitBonus: {
    isDepositExitBonus: boolean;
  };
  bonusBoostBonus: {
    isShowBonusBoost?: boolean;
    isShowBonusBoostActiveBonus?: boolean;
    bonusBoostCancelGroups?: string[];
    bonusBoostActivateGroup?: string;
    bonusBoostLimitTime?: number;
    remainingTime?: number;
    endTime?: number;
  };
}

@Injectable({
  providedIn: 'root',
})
export class CustomBonusService {
  private _userGroup = inject(UserGroupsService);
  private _static = inject(StaticContentService);
  private _user = inject(UserService);
  private _localStorage = inject(LocalstorageService);
  private _platform = inject(PlatformService);
  private _document = inject<Document>(DOCUMENT);
  private timerSubscription: Subscription | null = null;

  /**
   * Initial values
   * @private
   */
  private _state = signal<CustomBonus>({
    noDepositBonus: {
      isNoDepositFsBonus: false,
    },
    depositExitBonus: {
      isDepositExitBonus: false,
    },
    bonusBoostBonus: {
      isShowBonusBoost: false,
      isShowBonusBoostActiveBonus: false,
      bonusBoostCancelGroups: ['ID803', 'ID804', 'ID805'],
      bonusBoostActivateGroup: 'ID802',
      bonusBoostLimitTime: 300000,
      remainingTime: 0,
      endTime: null,
    },
  });

  /**
   * Select global state
   */
  public state = computed(() => this._state());

  /**
   * Select no deposit fs bonus state
   */
  public isNoDepositFsBonus = computed(
    () => this._state().noDepositBonus.isNoDepositFsBonus,
  );

  /**
   * Select deposit exit bonus state
   */
  public isDepositExitBonus = computed(
    () => this._state().depositExitBonus.isDepositExitBonus,
  );

  /**
   * Select show bonus boost bonus
   */
  public isShowBonusBoost = computed(
    () => this._state().bonusBoostBonus.isShowBonusBoost,
  );

  /**
   * Select remaining time bonus boost bonus in minutes
   */
  public remainingTime = computed(() =>
    Math.round(this._state().bonusBoostBonus.remainingTime / 60000),
  );

  /**
   * Select elapsed time bonus boost in percentage
   */
  public elapsedTimePercentage = computed(() => {
    const remaining = this._state().bonusBoostBonus.remainingTime;
    const limit = this._state().bonusBoostBonus.bonusBoostLimitTime;

    if (limit <= 0) return 0;
    const elapsedTime = limit - remaining;
    const percentage = (elapsedTime / limit) * 100;

    return Math.max(0, Math.min(100, Math.round(percentage)));
  });

  /**
   * Select human time left for finish bonus boost bonus
   */
  public endTime = computed(() => {
    const time = new Date(this._getStoredEndTime() || 0);
    const day = time.getDate().toString().padStart(2, '0');
    const month = (time.getMonth() + 1).toString().padStart(2, '0');
    const year = time.getFullYear();
    const hours = time.getHours().toString().padStart(2, '0');
    const minutes = time.getMinutes().toString().padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  });

  constructor() {
    this._updateNoDepositFsBonus();
    this._updateNoDepositExitBonus();
    this._updateShowBonusBoost();
    this._restoreTimerState();
    this._handleVisibilityChange();
    // this._userGroup.removeFromGroup(['ID802', 'ID803', 'ID804', 'ID805']).subscribe()
  }

  /**
   * Activate bonus boost bonus
   */
  public activateBonusBoost() {
    this._userGroup
      .addToGroup(this._state().bonusBoostBonus.bonusBoostActivateGroup)
      .pipe(
        tap(() => {
          this._saveRemainingTimeStorage(
            this._state().bonusBoostBonus.bonusBoostLimitTime,
          );
          this._updateRemainingTime(
            this._state().bonusBoostBonus.bonusBoostLimitTime,
          );
          this._startBonusBoostTimer();
          this._updateShowBonusBoostActiveState(true);
        }),
      )
      .subscribe();
  }

  /**
   * Cancel bonus boost bonus
   */
  public cancelBonusBoost() {
    const depositCount = this._user.stats.deposits_count;
    const groupIndex = Math.min(depositCount - 1, 2);

    this._userGroup
      .addToGroup(
        this._state().bonusBoostBonus.bonusBoostCancelGroups[groupIndex],
      ).pipe(
      switchMap(() => {
        if (this.state().bonusBoostBonus.isShowBonusBoostActiveBonus) {
          return this._userGroup.removeFromGroup(
            this._state().bonusBoostBonus.bonusBoostActivateGroup,
          );
        } else {
          return of(null);
        }
      }),
      tap(() => {
        this._stopTimer();
        this._updateShowBonusBoostStore(false);
        this._updateShowBonusBoostActiveState(false);
        this._localStorage.clearItem('bb');
      }),
    )
      .subscribe();
  }

  private _handleVisibilityChange() {
    if (this._platform.isBrowser) {
      this._document.addEventListener('visibilitychange', () => {
        if (this._document.visibilityState === 'visible') {
          this._saveRemainingTimeStorage(
            this._state().bonusBoostBonus.remainingTime,
          );
          this._updateRemainingTime(
            this._state().bonusBoostBonus.remainingTime,
          );
        }
      });
    }
  }

  /**
   * Start bonus boost timer
   * @private
   */
  private _startBonusBoostTimer() {
    this._stopTimer();
    if (this._platform.isBrowser) {
      this.timerSubscription = interval(1000).subscribe(() => {
        this._handleEndTimer();
      });
    }
  }

  private _handleEndTimer() {
    if (this._getStoredRemainingTime() > 0) {
      const time = this._state().bonusBoostBonus.remainingTime -= 1000;
      this._saveRemainingTimeStorage(time);
      this._updateRemainingTime(time);
    } else {
      this._stopTimer();
      this.cancelBonusBoost();
    }
  }

  /**
   * Stop bonus boost timer
   * @private
   */
  private _stopTimer() {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
      this.timerSubscription = null;
    }
  }

  private _getStoredRemainingTime(): number {
    const stored = this._localStorage.get('bb');
    if (!stored) return 0;
    try {
      const decoded = this._decodeData(stored);
      const data = JSON.parse(decoded);
      return data?.remainingTime || 0;
    } catch (e) {
      return 0;
    }
  }

  private _getStoredEndTime(): number {
    const stored = this._localStorage.get('bb');
    if (!stored) return 0;
    try {
      const decoded = this._decodeData(stored);
      const data = JSON.parse(decoded);
      return data?.endTime || 0;
    } catch (e) {
      return 0;
    }
  }

  private _saveEndTimeToStorage(endTime: number) {
    let storage = {};
    const stored = this._localStorage.get('bb');
    if (stored) {
      try {
        const decoded = this._decodeData(stored);
        storage = JSON.parse(decoded);
      } catch (e) {
        storage = {};
      }
    }
    const updatedData = JSON.stringify({
      ...storage,
      endTime,
    });
    this._localStorage.set('bb', this._encodeData(updatedData));
  }

  private _saveRemainingTimeStorage(remainingTime: number) {
    let storage = {};
    const stored = this._localStorage.get('bb');
    if (stored) {
      try {
        const decoded = this._decodeData(stored);
        storage = JSON.parse(decoded);
      } catch (e) {
        storage = {};
      }
    }
    const updatedData = JSON.stringify({
      ...storage,
      remainingTime,
    });
    this._localStorage.set('bb', this._encodeData(updatedData));
  }

  /**
   * Restore timer state
   * @private
   */
  private _restoreTimerState() {
    const isActiveBonus =
      this._state().bonusBoostBonus.isShowBonusBoostActiveBonus;
    const remainingTime = this._getStoredRemainingTime();

    if (remainingTime >= 0 && isActiveBonus) {
      this._updateRemainingTime(remainingTime);
      this._startBonusBoostTimer();
    } else if (isActiveBonus && remainingTime <= Date.now()) {
      this.cancelBonusBoost();
    }
  }

  /**
   * Update remaining time in state
   * @param time
   * @private
   */
  private _updateRemainingTime(time: number) {
    this._state.update((store) => ({
      ...store,
      bonusBoostBonus: {
        ...store.bonusBoostBonus,
        remainingTime: time,
      },
    }));
  }

  /**
   * Update global show bonus boost state
   * @private
   */
  private _updateShowBonusBoostStore(isShowBonusBoost: boolean) {
    const ONE_DAY_MS = 24 * 60 * 60 * 1000;
    const endTime = Date.now() + ONE_DAY_MS;

    this._state.update((store) => ({
      ...store,
      bonusBoostBonus: {
        ...store.bonusBoostBonus,
        isShowBonusBoost,
        endTime,
      },
    }));

    const rawStorage = this._localStorage.get('bb');
    let storage: any = {};
    if (rawStorage) {
      try {
        const decoded = this._decodeData(rawStorage);
        storage = JSON.parse(decoded);
      } catch (e) {
        storage = {};
      }
    }

    if (isShowBonusBoost && !storage.endTime) {
      storage.endTime = endTime;
      this._saveEndTimeToStorage(endTime);
    }

    if (storage.endTime && new Date() >= new Date(storage.endTime)) {
      this.cancelBonusBoost();
    }
  }

  /**
   * Update show bonus boost state
   * @private
   */
  private _updateShowBonusBoost() {
    const depositCount = this._user.stats.deposits_count;
    if (depositCount >= 1 && depositCount <= 3) {
      const groupIndex = depositCount - 1;
      const shouldShow = !this._userGroup.isExistGroup(
          this._state().bonusBoostBonus.bonusBoostCancelGroups[groupIndex],
        )
        &&
        AB_TEST_LIST.DEVBIT4622.resolvedValue ===
        AB_TEST_LIST.DEVBIT4622.abTestGroupIds.V2;
      this._updateShowBonusBoostStore(shouldShow);
    }
    if (
      this._userGroup.isExistGroup(
        this._state().bonusBoostBonus.bonusBoostActivateGroup,
      )
    ) {
      this._updateShowBonusBoostActiveState(true);
    }
  }

  /**
   * Update show bonus boost active state
   * @param isShowBonusBoostActiveBonus
   * @private
   */
  private _updateShowBonusBoostActiveState(
    isShowBonusBoostActiveBonus: boolean,
  ) {
    this._state.update((store) => ({
      ...store,
      bonusBoostBonus: {
        ...store.bonusBoostBonus,
        isShowBonusBoostActiveBonus,
      },
    }));
  }

  /**
   * Update show no deposit bonus
   * @private
   */
  private _updateNoDepositFsBonus() {
    this._static
      .item({ slug: 'ndfs' })
      .pipe(
        filter((data) => !!data),
        map((data) => Boolean(data?.length)),
      )
      .subscribe((isNoDepositFsBonus) => {
        if (isNoDepositFsBonus && !this._user.info.confirmed_at) {
          this._state.update((store) => ({
            ...store,
            noDepositBonus: {
              isNoDepositFsBonus: true,
            },
          }));
        }
      });
  }

  /**
   * Update show deposit exit bonus state
   * @private
   */
  private _updateNoDepositExitBonus() {
    if (
      this._userGroup.isExistGroup(DEPOSIT_EXIT_BONUS_GROUP) &&
      !this._userGroup.isExistGroup(ONE_PLUS_DEPOSITOR)
    ) {
      this._state.update((store) => ({
        ...store,
        depositExitBonus: {
          isDepositExitBonus: true,
        },
      }));
    }
  }

  private _encodeData(data: string): string {
    return data ? btoa(unescape(encodeURIComponent(data))) : '';
  }

  private _decodeData(encodedData: string): string {
    return encodedData ? decodeURIComponent(escape(atob(encodedData))) : '';
  }
}
